import React, { useEffect, useRef } from 'react'

const lightBlueBarColor = '#5BAAD6' // Using blue-400 from figmaColors which matches the Figma design

export type Analyzable = Pick<AnalyserNode, 'frequencyBinCount' | 'getByteFrequencyData'>

interface FrequencyGraphProps {
  analyser: Analyzable
  height: number
  barColor?: string
}

export function FrequencyGraph({ analyser, height, barColor = lightBlueBarColor }: FrequencyGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    let animationId: number

    const draw = () => {
      animationId = requestAnimationFrame(draw)
      analyser.getByteFrequencyData(dataArray)

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Show more bars to match Figma design (around 45-50 bars)
      const usableItems = Math.min(dataArray.length, 48)
      const totalBarWidth = 3 // Fixed thin bar width to match Figma
      const barSpacing = 5 // Consistent spacing between bars
      const centerY = canvas.height / 2

      // Center the frequency graph
      const totalWidth = usableItems * totalBarWidth + (usableItems - 1) * barSpacing
      const startX = (canvas.width - totalWidth) / 2

      for (let i = 0; i < usableItems; i++) {
        // More subtle emphasis curve for natural look
        const emphasis = 0.3 + Math.sin((i / usableItems) * Math.PI) * 0.7
        // Scale down the amplitude to match Figma's more subtle appearance
        const barHeight = Math.max(minBarHeight(canvas.height), (dataArray[i] / 255) * canvas.height * emphasis * 0.6)

        ctx.fillStyle = barColor
        const x = startX + i * (totalBarWidth + barSpacing)

        // Draw upper bar (rounded at top corners)
        roundedRect(
          ctx,
          x,
          centerY - barHeight,
          totalBarWidth,
          barHeight,
          Math.min(totalBarWidth / 2, barHeight / 2, 2),
          'top'
        )
        // Draw lower bar (rounded at bottom corners)
        roundedRect(ctx, x, centerY, totalBarWidth, barHeight, Math.min(totalBarWidth / 2, barHeight / 2, 2), 'bottom')
      }
    }

    draw()

    return () => {
      cancelAnimationFrame(animationId)
    }
  }, [analyser])

  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <canvas
        data-role="frequency-graph"
        ref={canvasRef}
        height={height}
        width={300}
        style={{ width: '100%', height: '100%', display: 'block' }}
        className="ph-no-capture"
      />
    </div>
  )
}

function minBarHeight(canvasHeight: number) {
  return Math.max(Math.ceil(canvasHeight / 20), 2) // More subtle minimum height
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  roundingPosition: 'top' | 'bottom' = 'top'
) {
  ctx.beginPath()
  if (roundingPosition === 'top') {
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height)
    ctx.lineTo(x, y + height)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
  } else {
    ctx.moveTo(x, y)
    ctx.lineTo(x + width, y)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y)
  }
  ctx.closePath()
  ctx.fill()
}
